import { supabase } from './supabase'

/**
 * Who is signed in, and therefore which interface they get.
 *
 * The role is read from the database, never from anything the client could
 * modify. It decides which shell renders — but rendering is only cosmetic:
 * the real enforcement is the row-level security policies, which apply to
 * this same session regardless of what the UI shows.
 */

export type Role = 'admin' | 'member'

export interface Space {
  id: string
  name: string
  kind: 'household' | 'business'
}

export interface FamilySession {
  userId: string
  email: string | null
  displayName: string
  role: Role
  accountId: string
  accountName: string
  spaces: Space[]
}

/** No family yet — the user must create one or redeem an invite. */
export const NO_FAMILY = 'no_family' as const
export type SessionResult = FamilySession | typeof NO_FAMILY | null

export async function loadSession(): Promise<SessionResult> {
  const { data: auth } = await supabase.auth.getUser()
  const user = auth.user
  if (!user) return null

  // Filter by user_id explicitly. The RLS policy on `memberships`
  // deliberately exposes EVERY membership in the account — the app needs that
  // to show who is in the family — so "the row I get back" is not "my row".
  //
  // Relying on RLS to narrow this worked only while the family had one member.
  // The moment a parent joined there were two rows, and the role read back was
  // whichever came first: the second person to join was handed the
  // administrator's interface, with the sender's screen nowhere to be found.
  //
  // Authorisation was never actually breached — every policy is enforced on
  // this same session in Postgres, so the wrong shell still could not read or
  // write anything it was not allowed to. But it is the wrong screen, and a
  // parent who cannot find "Document versturen" cannot use the app at all.
  const { data: memberships, error } = await supabase
    .from('memberships')
    .select('role, account_id, created_at, accounts(name)')
    .eq('user_id', user.id)
    .is('deleted_at', null)
    .order('created_at')
    .limit(1)

  if (error) throw error
  const membership = memberships?.[0]
  if (!membership) return NO_FAMILY

  const [{ data: profile }, { data: spaces }] = await Promise.all([
    supabase.from('users').select('display_name').eq('id', user.id).maybeSingle(),
    supabase.from('spaces').select('id, name, kind').is('deleted_at', null).order('name'),
  ])

  const account = membership.accounts as unknown as { name: string } | null

  return {
    userId: user.id,
    email: user.email ?? null,
    displayName: profile?.display_name || user.email?.split('@')[0] || '',
    role: membership.role as Role,
    accountId: membership.account_id,
    accountName: account?.name ?? 'Familie',
    spaces: (spaces ?? []) as Space[],
  }
}

export async function signOut() {
  await supabase.auth.signOut()
}

export async function createFamily(accountName: string, displayName: string) {
  const { error } = await supabase.rpc('bootstrap_family', {
    p_account_name: accountName,
    p_display_name: displayName,
  })
  if (error) throw error
}

export async function joinFamily(code: string) {
  const { error } = await supabase.rpc('redeem_invite', { p_code: code.trim() })
  if (error) throw error
}

export async function createInvite(displayName: string): Promise<string> {
  const { data, error } = await supabase.rpc('create_invite', {
    p_display_name: displayName,
    p_role: 'member',
  })
  if (error) throw error
  return data as string
}

export interface FamilyMember {
  userId: string
  displayName: string
  role: Role
}

/**
 * Who else is in the family, for handing a task to.
 *
 * The memberships policy deliberately returns everyone in the account rather
 * than only your own row, so the app can show who is here — see the comment in
 * 0002. This is the one place that legitimately wants all of them.
 */
export async function loadFamily(accountId: string): Promise<FamilyMember[]> {
  const { data, error } = await supabase
    .from('memberships')
    .select('user_id, role, users(display_name)')
    .eq('account_id', accountId)
    .is('deleted_at', null)
  if (error) throw error

  return (data ?? []).map((row) => ({
    userId: row.user_id as string,
    displayName:
      ((row.users as unknown as { display_name?: string })?.display_name || '').trim() ||
      'Familielid',
    role: row.role as Role,
  }))
}
